import { ComponentRef, EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { ConvertHelper } from '@barthauer/abc/core';
import {
  DataService as CoreDataService,
  ObjectTypeInfoColumnType,
  ObjectTypeInfoViewModel,
  QueryExpressionModel,
  ReadListDataSourceModel,
  ReadValidateModel,
} from '@barthauer/abc/core/data';
import { ResourceManager, ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { ParameterDefinition, ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { AbcCoreComponent, AbcCoreContainerService, AbcPopupComponent } from '@barthauer/abc/ui-components';
import {
  ExpressionEditorResult,
  ExpressionEditorSettings,
  OPathExpressionEditor,
  ValidationResult,
  ValidationType,
} from 'libs/abc/ui-components/src/lib/core/src/abc-grid/abc-grid';
import { BehaviorSubject, Subject } from 'rxjs';
import { concatMap, takeUntil, tap } from 'rxjs/operators';
import { ExpressionsEditorComponent } from './expressions-editor.component';
import { ComponentNameEnum } from './models/navigation.model';
import { ValidationSettings } from './models/validate.model';
import { UiEnum, UiSettings } from './models/view.model';
import { EditorService } from './services/editor.service';
import { OPathValidationService } from './services/oPath-validation.service';
import { SharedService } from './services/shared.service';

@Injectable({
  providedIn: 'root',
})
export class AbcExpressionsEditorService implements OnDestroy, OPathExpressionEditor {
  public onValidationResult: EventEmitter<ValidationResult> = new EventEmitter();
  public nodesTypeFilter: BehaviorSubject<string[]> = new BehaviorSubject([
    ObjectTypeInfoColumnType.IdRef,
    ObjectTypeInfoColumnType.PrimaryKey,
    ObjectTypeInfoColumnType.ObjectType,
    ObjectTypeInfoColumnType.Sys,
    undefined,
  ]);
  public resAsync: Promise<ResourceSet>;
  public exprssionInitialValue: string = '';
  public defaultParameterDefinitions: ParameterDefinition[] = [];

  private unsubscribe$ = new Subject<void>();
  private resourceManager = ResourceManager.getResources('abc-messages-messageBox');
  private executeValidationSubject: Subject<ReadValidateModel> = new Subject<ReadValidateModel>();

  constructor(
    private abcCoreContainer: AbcCoreContainerService,
    private coreDataService: CoreDataService,
    private resourcesService: ResourceManagerService,
    private messageBoxService: MessageBoxService,
    private validationService: OPathValidationService,

    private editorService: EditorService,
    private sharedService: SharedService
  ) {
    this.resAsync = this.resourcesService.getResourceSet('abc-opath-expressions');

    this.executeValidationSubject
      .asObservable()
      .pipe(
        concatMap((readValidateModel: ReadValidateModel) => {
          return this.coreDataService.executeValidateQuery(readValidateModel);
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((data: ArrayBuffer) => {
        const dataObject = ConvertHelper.arrayBufferToObject(data) as ValidationResult;
        const validationResult = new ValidationResult();
        validationResult.ValueCount = dataObject.ValueCount;
        validationResult.RefObjectTypeName = dataObject.RefObjectTypeName;
        validationResult.Errors = dataObject.Errors ?? [];
        this.onValidationResult.emit(validationResult);
        this.validationService.validationResult = validationResult;
      });

    this.sharedService.initializeTranslation();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  private async getDefaultParameterDefinitions(): Promise<ParameterDefinition[]> {
    const defaultParameterDefinitionQuery: ReadListDataSourceModel = new ReadListDataSourceModel();

    return ConvertHelper.arrayBufferToObjectArray(
      await this.coreDataService
        .executeDefaultParameterDefinitionsQuery(defaultParameterDefinitionQuery)
        .pipe(takeUntil(this.unsubscribe$))
        .toPromise()
    );
  }

  private createExpressionPopup(popUpTitle?: string): {
    popupComponent: AbcPopupComponent;
    popupContainer: AbcCoreComponent;
    expressionOpathCptRef: ComponentRef<ExpressionsEditorComponent>;
  } {
    let abcPopupComponent: AbcPopupComponent | undefined;
    let abcPopupContainer: AbcCoreComponent | undefined;
    abcPopupContainer = this.abcCoreContainer.createComponent(abcPopupContainer, AbcPopupComponent);
    abcPopupComponent = abcPopupContainer.componentRef.instance as AbcPopupComponent;
    abcPopupComponent.component = ExpressionsEditorComponent;
    abcPopupComponent.resizeEnabled = true;
    abcPopupComponent.width = UiSettings.popupWidth;
    abcPopupComponent.height = UiSettings.popupHeight;
    abcPopupComponent.minWidth = UiSettings.popupMinWidth;
    abcPopupComponent.minHeight = UiSettings.popupMinHeight;
    abcPopupComponent.title = popUpTitle;

    const expressionOpathCptRef = abcPopupComponent.openPopUp() as ComponentRef<ExpressionsEditorComponent>;

    return {
      popupComponent: abcPopupComponent,
      popupContainer: abcPopupContainer,
      expressionOpathCptRef: expressionOpathCptRef,
    };
  }

  public async openExpressionEditor(settings: ExpressionEditorSettings): Promise<void> {
    settings.editorInstanceSettings.editorId = 'editor' + this.editorService.editorIndex;
    settings.parameterDefinitions.forEach((_: ParameterDefinition) => (_.IsUserCreated = true));
    this.editorService.editorInstances.push(settings.editorInstanceSettings);
    this.validationService.validationType = settings.validationType;

    this.editorService.editorIndex++;

    const popup = this.createExpressionPopup(settings.title);
    const expressionEditorComponent = popup.expressionOpathCptRef.instance;
    expressionEditorComponent.objectTypeInfos = settings.objectTypeInfoViewModels;
    expressionEditorComponent.oPathExpressionValue = settings.oPath;
    expressionEditorComponent.columnTitle.initialValue = settings.columnTitle ?? '';
    expressionEditorComponent.validationType = settings.validationType;
    !this.defaultParameterDefinitions.length &&
      (this.defaultParameterDefinitions = await this.getDefaultParameterDefinitions());
    expressionEditorComponent.parameterDefinitions = [
      ...this.defaultParameterDefinitions,
      ...settings.parameterDefinitions,
    ];

    let showConfirmationDialog: boolean = true;

    expressionEditorComponent.onCloseClick.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      showConfirmationDialog = false;
      popup.popupComponent.instance.hide();
    });

    popup.popupComponent.onResize.subscribe(() => {
      expressionEditorComponent.editorWidth(UiEnum.editorContainer);
    });

    try {
      popup.popupComponent.onHiding
        .asObservable()
        .pipe(
          tap(async (e) => {
            if (
              showConfirmationDialog &&
              ((settings.oPath || '') !== expressionEditorComponent.oPathEditor?.textContent ||
                (expressionEditorComponent.columnTitle.currentValue.length &&
                  expressionEditorComponent.columnTitle.currentValue !==
                    expressionEditorComponent.columnTitle.initialValue))
            ) {
              (e as any).cancel = true;
              const _settings = new MessageSettings();
              _settings.mode = MessageMode.Question;
              _settings.icon = MessageBoxIcon.Question;
              _settings.button = MessageBoxButtons.YesNoCancel;
              _settings.message = this.resourceManager('Save');

              const result: DialogResult = await this.messageBoxService.showMessageBox(_settings).toPromise();

              if (DialogResult.No === result['result']) {
                this.closeAbcExpressionOpath(popup);
              } else if (DialogResult.Yes === result['result']) {
                const neutral =
                  (await this.editorService.getValue(new ParameterValue('neutralDesignationActive')).toPromise()) ===
                  'False'
                    ? false
                    : true;

                const queryExpression = new QueryExpressionModel();
                queryExpression.ObjectType =
                  settings.objectTypeInfoViewModels[settings.objectTypeInfoViewModels.length - 1].name;
                queryExpression.OPath = expressionEditorComponent.editor.nativeElement.innerText;

                let opath: string = expressionEditorComponent.editor.nativeElement.innerText;
                try {
                  if (!neutral) {
                    opath = ConvertHelper.toString(
                      await this.coreDataService
                        .executeUnlocalizeExpressionQuery(queryExpression)
                        .pipe(takeUntil(this.unsubscribe$))
                        .toPromise()
                    );
                  }
                } catch {}

                const readValidateModel = this.createReadValidateModel({
                  oPath: opath,
                  objectTypes: settings.objectTypeInfoViewModels,
                  validationType: settings.validationType,
                });

                this.coreDataService
                  .executeValidateQuery(readValidateModel)
                  .toPromise()
                  .then((data: ArrayBuffer) => {
                    const result = new ValidationResult(data);
                    if (
                      (result?.RefObjectTypeName && settings.validationType === ValidationType.Query) ||
                      (result?.ValueCount && settings.validationType === ValidationType.Column)
                    ) {
                      expressionEditorComponent.createResult(
                        opath,
                        expressionEditorComponent.parameterDefinitions,
                        result
                      );
                      this.editorService.editorInstances[
                        this.editorService.editorIndex - 1
                      ].expressionsEditorResultAction(this.validationService.expressionEditorResult);
                      this.closeAbcExpressionOpath(popup);
                      showConfirmationDialog = true;
                    } else {
                      this.navigateToValidation(expressionEditorComponent);
                    }
                  });
              } else if (DialogResult.Cancel === result['result']) {
                return;
              }
            } else {
              const result: ExpressionEditorResult = expressionEditorComponent.getResult();

              this.editorService.editorInstances[this.editorService.editorIndex - 1].expressionsEditorResultAction(
                result
              );

              this.closeAbcExpressionOpath(popup);
              showConfirmationDialog = true;
            }
          })
        )
        .toPromise();
    } catch (err) {
      console.log(err);
    }
  }

  public async closeAbcExpressionOpath(popup: {
    popupComponent: AbcPopupComponent;
    popupContainer: AbcCoreComponent;
    expressionOpathCptRef: ComponentRef<ExpressionsEditorComponent>;
  }): Promise<void> {
    popup.popupComponent && popup.popupComponent.instance.dispose();
    popup.popupComponent && popup.popupComponent.ngOnDestroy();
    popup.expressionOpathCptRef.destroy();
    popup.popupComponent = undefined;
    popup.popupContainer = undefined;
    this.validationService.validationResult = null;
    this.validationService.expressionEditorResult = null;
    this.editorService.editorIndex--;
    this.editorService.editorInstances.pop();
  }

  public async executeValidation(validationSettings: ValidationSettings): Promise<void> {
    const readValidateModel: ReadValidateModel = this.createReadValidateModel(validationSettings);
    this.executeValidationSubject.next(readValidateModel);
  }

  private navigateToValidation(expressionEditorComponent: ExpressionsEditorComponent): void {
    if (expressionEditorComponent.navigationComponent !== ComponentNameEnum.ValidateComponent) {
      expressionEditorComponent.loadNavigationComponent(ComponentNameEnum.ValidateComponent);
      expressionEditorComponent.startOPathValidation();
    }
  }

  private createReadValidateModel(validationSettings: ValidationSettings): ReadValidateModel {
    const readValidateModel: ReadValidateModel = new ReadValidateModel();
    const oPaths: string[] = [];

    if (validationSettings.objectTypes.length == 1) {
      readValidateModel.ObjectTypeName = validationSettings.objectTypes[0].name;
    } else {
      validationSettings.objectTypes.forEach((objectTypeInfo: ObjectTypeInfoViewModel) => {
        oPaths.push(`Type(${objectTypeInfo.name})`);
      });
    }
    if (validationSettings.validationType === ValidationType.Query) {
      if (validationSettings.oPath.split(' ').length > 1) {
        readValidateModel.OPath = [...oPaths, validationSettings.oPath, ''];
        return readValidateModel;
      }
    }
    readValidateModel.OPath = ['', ...oPaths, validationSettings.oPath];
    return readValidateModel;
  }
}
